﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcChartMap from '@mescius/wijmo.chart.map';
var WjFlexMapBehavior = /** @class */ (function (_super) {
    __extends(WjFlexMapBehavior, _super);
    function WjFlexMapBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexMapBehavior.prototype._updateControl = function (property, newValue) {
        switch (property) {
            case 'tooltipContent':
                this.control.tooltip.content = newValue;
                break;
            default:
                _super.prototype._updateControl.call(this, property, newValue);
        }
    };
    WjFlexMapBehavior.tag = 'wj-flex-map';
    WjFlexMapBehavior.props = [
        'isDisabled',
        'tabOrder',
        'binding',
        'footer',
        'header',
        'selectionMode',
        'options',
        'palette',
        'plotMargin',
        'footerStyle',
        'headerStyle',
        'tooltipContent',
        'itemsSource',
        'center',
        'zoom'
    ];
    WjFlexMapBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'rendering',
        'rendered',
        'selectionChanged',
        'itemsSourceChanging',
        'itemsSourceChanged'
    ];
    WjFlexMapBehavior.classCtor = function () { return wjcChartMap.FlexMap; };
    return WjFlexMapBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.map.FlexMap} control.
 *
 * The <b>wj-flex-map</b> component may contain
 * the following child components:
 * {@link wijmo.vue2.chart.WjFlexChartLegend}
 * , {@link wijmo.vue2.chart.map.WjScatterMapLayer}
 * , {@link wijmo.vue2.chart.map.WjGeoMapLayer}
 * and {@link wijmo.vue2.chart.map.WjGeoGridLayer}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.map.FlexMap} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexMap = WjFlexMapBehavior.register();
function registerV3WjFlexMap(app) {
    app.component(WjFlexMapBehavior.tag, WjFlexMap);
}
var WjScatterMapLayerBehavior = /** @class */ (function (_super) {
    __extends(WjScatterMapLayerBehavior, _super);
    function WjScatterMapLayerBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjScatterMapLayerBehavior.tag = 'wj-scatter-map-layer';
    WjScatterMapLayerBehavior.parentProp = 'layers';
    WjScatterMapLayerBehavior.siblingId = 'layers';
    WjScatterMapLayerBehavior.props = [
        'wjProperty',
        'itemsSource',
        'url',
        'symbolSize',
        'symbolMinSize',
        'symbolMaxSize',
        'binding'
    ];
    WjScatterMapLayerBehavior.events = [
        'initialized'
    ];
    WjScatterMapLayerBehavior.changeEvents = {
        'itemsSourceChanged': ['itemsSource'],
    };
    WjScatterMapLayerBehavior.classCtor = function () { return wjcChartMap.ScatterMapLayer; };
    return WjScatterMapLayerBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.map.ScatterMapLayer} class.
 *
 * The <b>wj-scatter-map-layer</b> component should be contained in
 * a {@link wijmo.vue2.chart.map.WjFlexMap} component.
 *
 * The <b>wj-scatter-map-layer</b> component may contain
 * a {@link wijmo.vue2.chart.map.WjColorScale} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.map.ScatterMapLayer} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjScatterMapLayer = WjScatterMapLayerBehavior.register();
function registerV3WjScatterMapLayer(app) {
    app.component(WjScatterMapLayerBehavior.tag, WjScatterMapLayer);
}
var WjGeoMapLayerBehavior = /** @class */ (function (_super) {
    __extends(WjGeoMapLayerBehavior, _super);
    function WjGeoMapLayerBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjGeoMapLayerBehavior.tag = 'wj-geo-map-layer';
    WjGeoMapLayerBehavior.parentProp = 'layers';
    WjGeoMapLayerBehavior.siblingId = 'layers';
    WjGeoMapLayerBehavior.props = [
        'wjProperty',
        'itemsSource',
        'url',
        'symbolSize',
        'itemFormatter'
    ];
    WjGeoMapLayerBehavior.events = [
        'initialized'
    ];
    WjGeoMapLayerBehavior.changeEvents = {
        'itemsSourceChanged': ['itemsSource'],
    };
    WjGeoMapLayerBehavior.classCtor = function () { return wjcChartMap.GeoMapLayer; };
    return WjGeoMapLayerBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.map.GeoMapLayer} class.
 *
 * The <b>wj-geo-map-layer</b> component should be contained in
 * a {@link wijmo.vue2.chart.map.WjFlexMap} component.
 *
 * The <b>wj-geo-map-layer</b> component may contain
 * a {@link wijmo.vue2.chart.map.WjColorScale} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.map.GeoMapLayer} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjGeoMapLayer = WjGeoMapLayerBehavior.register();
function registerV3WjGeoMapLayer(app) {
    app.component(WjGeoMapLayerBehavior.tag, WjGeoMapLayer);
}
var WjGeoGridLayerBehavior = /** @class */ (function (_super) {
    __extends(WjGeoGridLayerBehavior, _super);
    function WjGeoGridLayerBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjGeoGridLayerBehavior.tag = 'wj-geo-grid-layer';
    WjGeoGridLayerBehavior.parentProp = 'layers';
    WjGeoGridLayerBehavior.siblingId = 'layers';
    WjGeoGridLayerBehavior.props = [
        'wjProperty',
        'itemsSource',
        'url'
    ];
    WjGeoGridLayerBehavior.events = [
        'initialized'
    ];
    WjGeoGridLayerBehavior.changeEvents = {
        'itemsSourceChanged': ['itemsSource'],
    };
    WjGeoGridLayerBehavior.classCtor = function () { return wjcChartMap.GeoGridLayer; };
    return WjGeoGridLayerBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.map.GeoGridLayer} class.
 *
 * The <b>wj-geo-grid-layer</b> component should be contained in
 * a {@link wijmo.vue2.chart.map.WjFlexMap} component.
 *
 * The <b>wj-geo-grid-layer</b> component may contain
 * a {@link wijmo.vue2.chart.map.WjColorScale} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.map.GeoGridLayer} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjGeoGridLayer = WjGeoGridLayerBehavior.register();
function registerV3WjGeoGridLayer(app) {
    app.component(WjGeoGridLayerBehavior.tag, WjGeoGridLayer);
}
var WjColorScaleBehavior = /** @class */ (function (_super) {
    __extends(WjColorScaleBehavior, _super);
    function WjColorScaleBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjColorScaleBehavior.tag = 'wj-color-scale';
    WjColorScaleBehavior.parentProp = 'colorScale';
    WjColorScaleBehavior.props = [
        'wjProperty',
        'scale',
        'binding',
        'colorUnknown',
        'colors',
        'format'
    ];
    WjColorScaleBehavior.events = [
        'initialized'
    ];
    WjColorScaleBehavior.classCtor = function () { return wjcChartMap.ColorScale; };
    return WjColorScaleBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.map.ColorScale} class.
 *
 * The <b>wj-color-scale</b> component should be contained in
 * one of the following components:
 * {@link wijmo.vue2.chart.map.WjScatterMapLayer}
 * , {@link wijmo.vue2.chart.map.WjGeoMapLayer}
 *  or {@link wijmo.vue2.chart.map.WjGeoGridLayer}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.map.ColorScale} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjColorScale = WjColorScaleBehavior.register();
function registerV3WjColorScale(app) {
    app.component(WjColorScaleBehavior.tag, WjColorScale);
}
export function registerChartMap(app) {
    if (VueApi.isV3Plus) {
        registerV3WjFlexMap(app);
        registerV3WjScatterMapLayer(app);
        registerV3WjGeoMapLayer(app);
        registerV3WjGeoGridLayer(app);
        registerV3WjColorScale(app);
    }
}
